/**
 * "Copy for NZPVS" (cca-uzt5) — the pure half of the lodgement card's copy
 * control (NzpvsCopyButton.tsx): the words it shows, the check it makes on what
 * the worker sent, and the rule for holding a copy between two clicks.
 *
 * The worker route (POST …/registrar/record/:id/nzpvs-copy) builds the payload
 * from the same row the CSV uses and writes an audit row before it answers.
 * Every sentence here names what happened and what to do — never a value from
 * the application (SEC-5).
 */

/** The control's label. */
export const NZPVS_COPY_LABEL = 'Copy for NZPVS';

/** The label while a fetched copy waits for a click the browser will let write. */
export const NZPVS_CLICK_AGAIN_LABEL = 'Click again to copy';

/** The status line beside that label, so the state is said as well as labelled (Miranda P-5). */
export const NZPVS_READY = 'Ready — click again to copy.';

/**
 * What the row says once the copy is on the clipboard. The copy stays there
 * until the bookmark takes it — the clipboard is cleared from inside the paste
 * on the Police page, not before (registrar, 29 Oct 2026: accepted for now) —
 * so the line says so, and says where it must not go.
 */
export const NZPVS_COPIED =
  "Copied. On the Police page, click your CCAANZ → NZPVS bookmark and paste. It stays on your clipboard until the bookmark takes it — don't paste it anywhere else.";

/**
 * What a row that copied EARLIER says once another row's copy has replaced it
 * on the clipboard (Miranda M-S1): one row at a time holds the clipboard.
 */
export const NZPVS_COPIED_EARLIER = 'Copied earlier — no longer on the clipboard.';

/** Every refusal opens with this, in the page's error ink (Miranda M-S1). */
export const NZPVS_NOT_COPIED = 'Not copied —';

/** The fallback sentence: any failure without a more particular one. */
export const NZPVS_COPY_FAILED = `${NZPVS_NOT_COPIED} something went wrong. Try again.`;

/**
 * A served copy of ANOTHER version than this page reads (Miranda r1 S-7): the
 * worker and this page are from different releases — almost always a tab left
 * open across a deploy. "Try again" would loop, and each retry writes another
 * served audit row; a reload is what fixes it.
 */
export const NZPVS_PAGE_OUT_OF_DATE = `${NZPVS_NOT_COPIED} this page is out of date. Reload it, then copy again.`;

/**
 * How long a fetched copy may wait for the second click. A clipboard write
 * needs the click's own "user activation", which a slow fetch can outlive
 * (Safari refuses any write after an await); the copy is then held — in a ref,
 * never React state or storage — until the next click or this, whichever
 * comes first.
 */
export const PENDING_COPY_TTL_MS = 60_000;

/** The payload marker and the version this portal's filler reads (bookmarklet.ts
 *  NZPVS_FILLER_VERSION; the worker's NZPVS_PAYLOAD_VERSION) — moved together. */
const MARKER = 'cca-nzpvs';
const VERSION = 3;

/** The route for one record's copy. */
export function nzpvsCopyPath(recordId: string): string {
  return `/api/vetting/registrar/record/${encodeURIComponent(recordId)}/nzpvs-copy`;
}

/** The worker's refusal codes, in the registrar's words — each opening "Not copied —". */
const ERROR_SENTENCES: Readonly<Record<string, string>> = {
  not_lodgeable: `${NZPVS_NOT_COPIED} this application is missing a required field.`,
  not_pending: `${NZPVS_NOT_COPIED} this application is no longer waiting to be lodged. Reload the page.`,
  audit_unavailable: `${NZPVS_NOT_COPIED} the copy couldn't be recorded. Try again, or use the CSV.`,
};

/**
 * The sentence for a failed request: apiPost throws an Error carrying the
 * parsed body as `data`; the worker's `{ error }` code picks the sentence.
 * Anything else — a network failure, a 403, an unknown code — is the fallback.
 */
export function copyErrorSentence(err: unknown): string {
  const data = (err as { data?: unknown } | null)?.data;
  const code =
    data !== null && typeof data === 'object' ? (data as { error?: unknown }).error : undefined;
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(ERROR_SENTENCES, code)) {
    return ERROR_SENTENCES[code] ?? NZPVS_COPY_FAILED;
  }
  return NZPVS_COPY_FAILED;
}

/**
 * The clipboard text for `payload` — but only if it is a copy of THIS
 * portal's version for THIS row; null otherwise, and nothing is copied. A
 * belt to the route's braces: the filler on the Police page refuses a wrong
 * marker too, but a copy for another applicant would look like a good one.
 */
export function payloadTextFor(payload: unknown, recordId: string): string | null {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const p = payload as Record<string, unknown>;
  if (p[MARKER] !== VERSION) return null;
  if (typeof p.pvrId !== 'string' || p.pvrId !== recordId) return null;
  return JSON.stringify(payload);
}

/**
 * The sentence for a served payload payloadTextFor refused: a CCAANZ copy of
 * another version (higher or lower) → NZPVS_PAGE_OUT_OF_DATE; anything else
 * (another row, no marker, not an object) → the fallback.
 */
export function payloadRefusalSentence(payload: unknown): string {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return NZPVS_COPY_FAILED;
  }
  const p = payload as Record<string, unknown>;
  return Object.prototype.hasOwnProperty.call(p, MARKER) && p[MARKER] !== VERSION
    ? NZPVS_PAGE_OUT_OF_DATE
    : NZPVS_COPY_FAILED;
}

/** A copy held for the second click. */
export interface PendingCopy {
  readonly text: string;
  readonly heldAt: number;
}

export function holdPendingCopy(text: string, now: number): PendingCopy {
  return { text, heldAt: now };
}

/**
 * The held text if it is still fresh, else null. The caller drops what it
 * holds either way: a held copy is written once or not at all.
 */
export function takePendingCopy(pending: PendingCopy | null, now: number): string | null {
  if (!pending) return null;
  const age = now - pending.heldAt;
  return age >= 0 && age < PENDING_COPY_TTL_MS ? pending.text : null;
}
